<template>
  <div class="storage-questions-tab">
    <!-- Фильтры: применяются по «Применить», состояние — в query-строке URL -->
    <b-card no-body class="mb-1">
      <b-card-body class="pb-50">
        <b-row>
          <b-col md="3" class="mb-1">
            <b-form-input
              v-model="filters.search"
              placeholder="Текст вопроса / ответа"
              @keyup.enter="applyFilters"
            />
          </b-col>
          <b-col md="3" class="mb-1">
            <v-select
              v-model="artistOption"
              :options="artistOptions"
              :filterable="false"
              label="label"
              placeholder="Исполнитель"
              @search="onArtistSearch"
            >
              <template #no-options>Начните вводить название</template>
            </v-select>
          </b-col>
          <b-col md="3" class="mb-1">
            <v-select
              v-model="movieOption"
              :options="movieOptions"
              :filterable="false"
              label="label"
              placeholder="Фильм / сериал"
              @search="onMovieSearch"
            >
              <template #no-options>Начните вводить название</template>
            </v-select>
          </b-col>
          <b-col md="3" class="mb-1">
            <v-select
              :value="selectedMechanic"
              :options="mechanicOptions"
              label="name"
              placeholder="Механика"
              :disabled="withoutMechanic"
              @input="option => { filters.mechanic_id = option ? String(option.id) : '' }"
            />
          </b-col>

          <b-col md="2" class="mb-1">
            <b-form-select v-model="filters.kind" :options="kindOptions" />
          </b-col>
          <b-col md="2" class="mb-1">
            <v-select
              :value="selectedDecade"
              :options="decadeOptions"
              label="name"
              placeholder="Декада"
              @input="option => { filters.decade = option ? String(option.value) : '' }"
            />
          </b-col>
          <b-col md="3" class="mb-1">
            <v-select
              :value="selectedCluster"
              :options="clusterOptions"
              label="title"
              placeholder="Жанр"
              @input="option => { filters.cluster_id = option ? String(option.id) : '' }"
            />
          </b-col>
          <b-col md="2" class="mb-1">
            <v-select
              :value="selectedOrigin"
              :options="originOptions"
              label="name"
              placeholder="Ру / зарубеж"
              @input="option => { filters.origin = option ? option.code : '' }"
            />
          </b-col>
          <b-col md="3" class="mb-1">
            <v-select
              :value="selectedPack"
              :options="packOptions"
              label="name"
              placeholder="Пакет"
              @input="option => { filters.game_id = option ? String(option.id) : '' }"
            />
          </b-col>

          <b-col md="3" class="mb-1">
            <b-form-select v-model="filters.played" :options="playedOptions" />
          </b-col>
          <b-col md="4" class="mb-1">
            <div class="d-flex align-items-center">
              <span class="text-nowrap mr-50 small">Премьера</span>
              <b-form-input v-model="filters.premiere_from" type="date" size="sm" class="mr-25" />
              <span class="mr-25">–</span>
              <b-form-input v-model="filters.premiere_to" type="date" size="sm" />
            </div>
          </b-col>
          <b-col md="5" class="mb-1">
            <div class="d-flex align-items-center">
              <span class="text-nowrap mr-50 small">Посл. отыгрыш</span>
              <b-form-input v-model="filters.last_played_from" type="date" size="sm" class="mr-25" />
              <span class="mr-25">–</span>
              <b-form-input v-model="filters.last_played_to" type="date" size="sm" />
            </div>
          </b-col>
        </b-row>

        <div class="d-flex align-items-center flex-wrap mb-50">
          <b-form-checkbox v-model="withoutArtist" class="mr-2">без исполнителя/фильма</b-form-checkbox>
          <b-form-checkbox v-model="withoutMechanic" class="mr-2">без механики</b-form-checkbox>
          <b-button variant="primary" size="sm" class="mr-50" @click="applyFilters">Применить</b-button>
          <b-button variant="flat-secondary" size="sm" @click="resetFilters">Сбросить</b-button>
          <span class="ml-auto text-muted small">Найдено: {{ total }}</span>
        </div>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-table
        :items="rows"
        :fields="tableColumns"
        primary-key="id"
        responsive
        hover
        show-empty
        empty-text="Ничего не найдено"
        class="position-relative mb-0 storage-questions-table"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        no-local-sorting
        :busy="loading"
        @sort-changed="onSortChanged"
        @row-clicked="openQuestion"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-50" />
            <strong>Загрузка...</strong>
          </div>
        </template>

        <template #cell(parts)="data">
          <span class="text-nowrap">
            <feather-icon
              v-for="(type, index) in data.item.parts_summary.question"
              :key="`q-${index}`"
              :icon="partIcon(type)"
              size="14"
              class="mr-25"
            />
            <template v-if="data.item.parts_summary.answer.length">
              <span class="text-muted mx-25">→</span>
              <feather-icon
                v-for="(type, index) in data.item.parts_summary.answer"
                :key="`a-${index}`"
                :icon="partIcon(type)"
                size="14"
                class="mr-25 text-muted"
              />
            </template>
          </span>
          <div v-if="data.item.question" class="small text-muted text-truncate question-preview">{{ data.item.question }}</div>
        </template>

        <template #cell(answer)="data">
          <div class="answer-cell">{{ data.item.answer || '—' }}</div>
          <b-badge v-if="data.item.kind === 'quiz'" variant="light-info">квиз</b-badge>
        </template>

        <template #cell(mechanic)="data">
          {{ data.item.mechanic ? data.item.mechanic.name : '—' }}
        </template>

        <template #cell(entities)="data">
          <span v-if="!data.item.artists.length && !data.item.movies.length" class="text-muted">—</span>
          <div v-else class="entities-cell">
            {{ entitiesText(data.item) }}
          </div>
        </template>

        <template #cell(placement)="data">
          <span v-if="!data.item.game" class="text-muted">—</span>
          <span v-else>
            {{ data.item.game.name }}<template v-if="data.item.theme"> · {{ data.item.theme }}</template><template v-if="data.item.price !== null"> · {{ data.item.price }}</template>
          </span>
        </template>

        <template #cell(premiere_at)="data">
          {{ formatDate(data.item.premiere_at) }}
        </template>

        <template #cell(last_played_at)="data">
          {{ formatDate(data.item.last_played_at) }}
          <b-badge v-if="!data.item.is_played" variant="light-secondary" class="ml-25">не сыгран</b-badge>
        </template>
      </b-table>

      <b-card-body class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <label class="text-nowrap mb-0 mr-50 font-small-3">На странице:</label>
          <v-select
            :value="perPage"
            :options="perPageOptions"
            :clearable="false"
            style="min-width: 80px"
            @input="onPerPageInput"
          />
        </div>
        <b-pagination
          v-if="total > 0"
          :value="page"
          :total-rows="total"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0"
          prev-class="prev-item"
          next-class="next-item"
          @input="onPageInput"
        />
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BCardBody, BCol, BFormCheckbox, BFormInput, BFormSelect, BPagination, BRow, BSpinner, BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { computed, ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'

const MODULE = 'question-storage'
const TAB = 'questions'
const DEFAULT_SORT = '-premiere_at'
const DEFAULT_PER_PAGE = 30
const MECHANIC_NONE = 'none'

// Ключи фильтров = query-параметры GET question-storage/questions (QuestionStorageQuery::applyFilters)
const getDefaultFilters = () => ({
  search: '',
  artist_id: '',
  movie_id: '',
  mechanic_id: '',
  kind: '',
  decade: '',
  cluster_id: '',
  origin: '',
  game_id: '',
  played: '',
  premiere_from: '',
  premiere_to: '',
  last_played_from: '',
  last_played_to: '',
  without_artist: '',
})
const FILTER_KEYS = Object.keys(getDefaultFilters())
// Подписи выбранных исполнителя/фильма — только для URL, бэкенд их не читает
const LABEL_KEYS = { artist_id: 'artist_name', movie_id: 'movie_name' }

const formatDate = value => {
  if (!value) return '—'
  const [year, month, day] = String(value).slice(0, 10).split('-')

  return `${day}.${month}.${year}`
}

export default {
  components: {
    BBadge, BButton, BCard, BCardBody, BCol, BFormCheckbox, BFormInput, BFormSelect, BPagination, BRow, BSpinner, BTable, vSelect,
  },
  setup(props, { root }) {
    const toast = useToast()
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })

    const filters = ref(getDefaultFilters())
    const artistOption = ref(null)
    const movieOption = ref(null)
    const artistOptions = ref([])
    const movieOptions = ref([])
    const page = ref(1)
    const perPage = ref(DEFAULT_PER_PAGE)
    const sort = ref(DEFAULT_SORT)
    const rows = ref([])
    const total = ref(0)
    const loading = ref(false)
    const lookups = ref({})
    const packs = ref([])
    let lastQueryKey = null
    let fetchNumber = 0

    const perPageOptions = [30, 50, 100]
    const kindOptions = [
      { value: '', text: 'Тип: все' },
      { value: 'svoya_igra', text: 'Своя игра' },
      { value: 'quiz', text: 'Квиз' },
    ]
    const playedOptions = [
      { value: '', text: 'Сыгранные и несыгранные' },
      { value: '1', text: 'Только сыгранные' },
      { value: '0', text: 'Только несыгранные' },
    ]

    const tableColumns = [
      { key: 'parts', label: 'Вопрос' },
      { key: 'answer', label: 'Ответ' },
      { key: 'mechanic', label: 'Механика' },
      { key: 'entities', label: 'Исполнители / фильмы' },
      { key: 'placement', label: 'Пакет · тема · цена' },
      { key: 'premiere_at', label: 'Премьера', sortable: true },
      { key: 'last_played_at', label: 'Посл. отыгрыш', sortable: true },
    ]

    // --- Справочники ---
    store.dispatch(`${MODULE}/fetchLookups`)
      .then(response => { lookups.value = response.data.data || {} })
      .catch(() => showError('Не удалось загрузить справочники'))
    store.dispatch(`${MODULE}/fetchPacks`, { per_page: 100 })
      .then(response => { packs.value = response.data.data || [] })
      .catch(() => showError('Не удалось загрузить пакеты'))

    const mechanicOptions = computed(() => lookups.value.mechanics || [])
    const decadeOptions = computed(() => lookups.value.decades || [])
    const clusterOptions = computed(() => lookups.value.clusters || [])
    const originOptions = computed(() => lookups.value.origins || [])
    const packOptions = computed(() => {
      const gameId = parseInt(filters.value.game_id, 10)
      if (gameId > 0 && !packs.value.some(pack => pack.id === gameId)) {
        return [{ id: gameId, name: `Пакет #${gameId}` }, ...packs.value]
      }

      return packs.value
    })

    const selectedMechanic = computed(() => mechanicOptions.value.find(mechanic => String(mechanic.id) === filters.value.mechanic_id) || null)
    const selectedDecade = computed(() => decadeOptions.value.find(decade => String(decade.value) === filters.value.decade) || null)
    const selectedCluster = computed(() => clusterOptions.value.find(cluster => String(cluster.id) === filters.value.cluster_id) || null)
    const selectedOrigin = computed(() => originOptions.value.find(origin => origin.code === filters.value.origin) || null)
    const selectedPack = computed(() => packOptions.value.find(pack => String(pack.id) === filters.value.game_id) || null)

    const withoutMechanic = computed({
      get: () => filters.value.mechanic_id === MECHANIC_NONE,
      set: checked => { filters.value.mechanic_id = checked ? MECHANIC_NONE : '' },
    })
    const withoutArtist = computed({
      get: () => filters.value.without_artist === '1',
      set: checked => { filters.value.without_artist = checked ? '1' : '' },
    })

    watch(artistOption, option => { filters.value.artist_id = option ? String(option.id) : '' })
    watch(movieOption, option => { filters.value.movie_id = option ? String(option.id) : '' })

    // --- Поиск исполнителя / фильма для фильтра ---
    let artistSearchNumber = 0
    const onArtistSearch = (query, loadingCallback) => {
      if (!query || query.length < 2) return
      artistSearchNumber += 1
      const requestNumber = artistSearchNumber
      loadingCallback(true)
      store.dispatch(`${MODULE}/searchArtists`, query)
        .then(response => {
          if (requestNumber !== artistSearchNumber) return
          artistOptions.value = (response.data.data || []).map(artist => ({ id: artist.id, label: artist.title }))
        })
        .catch(() => showError('Ошибка поиска исполнителей'))
        .finally(() => loadingCallback(false))
    }

    let movieSearchNumber = 0
    const onMovieSearch = (query, loadingCallback) => {
      if (!query || query.length < 2) return
      movieSearchNumber += 1
      const requestNumber = movieSearchNumber
      loadingCallback(true)
      store.dispatch(`${MODULE}/searchMovies`, query)
        .then(response => {
          if (requestNumber !== movieSearchNumber) return
          movieOptions.value = (response.data.data || []).map(movie => ({ id: movie.id, label: movie.title_ru || movie.title_en }))
        })
        .catch(() => showError('Ошибка поиска фильмов'))
        .finally(() => loadingCallback(false))
    }

    // --- URL ↔ состояние ---
    const readFromQuery = query => {
      const nextFilters = getDefaultFilters()
      FILTER_KEYS.forEach(key => {
        if (query[key] !== undefined && query[key] !== null) nextFilters[key] = String(query[key])
      })
      filters.value = nextFilters
      artistOption.value = nextFilters.artist_id
        ? { id: parseInt(nextFilters.artist_id, 10), label: query.artist_name || `Исполнитель #${nextFilters.artist_id}` }
        : null
      movieOption.value = nextFilters.movie_id
        ? { id: parseInt(nextFilters.movie_id, 10), label: query.movie_name || `Фильм #${nextFilters.movie_id}` }
        : null
      page.value = parseInt(query.page, 10) || 1
      perPage.value = perPageOptions.includes(parseInt(query.per_page, 10)) ? parseInt(query.per_page, 10) : DEFAULT_PER_PAGE
      sort.value = query.sort ? String(query.sort) : DEFAULT_SORT
    }

    // Параметры запроса к API: только непустые фильтры
    const buildApiParams = () => {
      const params = { page: page.value, per_page: perPage.value, sort: sort.value }
      FILTER_KEYS.forEach(key => {
        if (filters.value[key] !== '') params[key] = filters.value[key]
      })

      return params
    }

    const buildUrlQuery = () => {
      const query = { tab: TAB }
      Object.entries(buildApiParams()).forEach(([key, value]) => { query[key] = String(value) })
      if (artistOption.value) query[LABEL_KEYS.artist_id] = artistOption.value.label
      if (movieOption.value) query[LABEL_KEYS.movie_id] = movieOption.value.label
      if (query.page === '1') delete query.page
      if (query.per_page === String(DEFAULT_PER_PAGE)) delete query.per_page
      if (query.sort === DEFAULT_SORT) delete query.sort

      return query
    }

    const queryKey = query => JSON.stringify(Object.keys(query).sort().map(key => [key, String(query[key])]))

    const fetchList = () => {
      const urlQuery = buildUrlQuery()
      lastQueryKey = queryKey(urlQuery)
      if (queryKey(root.$route.query) !== lastQueryKey) root.$router.replace({ query: urlQuery }).catch(() => {})

      fetchNumber += 1
      const requestNumber = fetchNumber
      loading.value = true
      store.dispatch(`${MODULE}/fetchQuestions`, buildApiParams())
        .then(response => {
          if (requestNumber !== fetchNumber) return
          rows.value = response.data.data || []
          total.value = response.data.total || 0
        })
        .catch(() => {
          if (requestNumber === fetchNumber) showError('Не удалось загрузить вопросы')
        })
        .finally(() => {
          if (requestNumber === fetchNumber) loading.value = false
        })
    }

    const applyFilters = () => {
      page.value = 1
      fetchList()
    }

    const resetFilters = () => {
      filters.value = getDefaultFilters()
      artistOption.value = null
      movieOption.value = null
      applyFilters()
    }

    const onPageInput = value => {
      page.value = value
      fetchList()
    }

    const onPerPageInput = value => {
      perPage.value = value
      applyFilters()
    }

    // --- Сортировка: ключ колонки = поле sort на бэке, «-» — по убыванию ---
    const sortBy = computed(() => sort.value.replace(/^-/, ''))
    const sortDesc = computed(() => sort.value.startsWith('-'))
    const onSortChanged = context => {
      if (!context.sortBy) return
      sort.value = `${context.sortDesc ? '-' : ''}${context.sortBy}`
      applyFilters()
    }

    readFromQuery(root.$route.query)
    fetchList()

    // Назад/вперёд в браузере или переход сюда же с другими фильтрами — перечитываем URL
    watch(() => root.$route.query, query => {
      if (root.$route.name !== 'question-storage' || (query.tab || TAB) !== TAB) return
      if (queryKey(query) === lastQueryKey) return
      readFromQuery(query)
      fetchList()
    })

    const openQuestion = item => {
      root.$router.push({ name: 'question-storage-edit', params: { id: item.id } })
    }

    const partIcons = {
      audio: 'MusicIcon', image: 'ImageIcon', video: 'FilmIcon', text: 'TypeIcon',
    }
    const partIcon = type => partIcons[type] || 'FileIcon'

    const entitiesText = item => [
      ...item.artists.map(artist => artist.title),
      ...item.movies.map(movie => movie.title_ru),
    ].filter(Boolean).join(', ')

    return {
      filters,
      artistOption,
      movieOption,
      artistOptions,
      movieOptions,
      page,
      perPage,
      perPageOptions,
      rows,
      total,
      loading,
      kindOptions,
      playedOptions,
      tableColumns,
      mechanicOptions,
      decadeOptions,
      clusterOptions,
      originOptions,
      packOptions,
      selectedMechanic,
      selectedDecade,
      selectedCluster,
      selectedOrigin,
      selectedPack,
      withoutMechanic,
      withoutArtist,
      onArtistSearch,
      onMovieSearch,
      applyFilters,
      resetFilters,
      onPageInput,
      onPerPageInput,
      sortBy,
      sortDesc,
      onSortChanged,
      openQuestion,
      partIcon,
      entitiesText,
      formatDate,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
.storage-questions-table >>> tbody tr {
  cursor: pointer;
}
.question-preview {
  max-width: 260px;
}
.answer-cell {
  max-width: 280px;
  white-space: pre-line;
}
.entities-cell {
  max-width: 240px;
}
</style>
